import React, { useEffect, useState } from 'react';
import './TeamPage.scss';
import Loader from '../../../shared/components/common/loader/Loader';
import { getTeam, removeUser } from './services/TeamService';
import { TeamResponse } from './models/TeamResponse';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getTokenData } from '../../../shared/services/auth/AuthService';
import { TokenData } from '../../../shared/models/auth/TokenData';
import { isAdminOrOwner } from '../../../shared/utils/utils';
import { useNavigate } from 'react-router-dom';

const TeamPage: React.FC = () => {
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const navigate = useNavigate();
  const [team, setTeam] = useState<TeamResponse[] | null>(null);
  const tokenData: TokenData | null = getTokenData();

  useEffect(() => {
    getTeam().then((data) => setTeam(data));
  }, []);

  const permissionsMap = new Map([
    ['ADMIN', t('admin')],
    ['USER', t('user')],
    ['OWNER', t('owner')],
  ]);

  const confirmRemove = (member: TeamResponse) => {
    confirmDialog({
      message: t('areYouSureProceed'),
      header: t('areYouSure'),
      defaultFocus: 'accept',
      accept: () => remove(member),
    });
  };

  const remove = (member: TeamResponse) => {
    removeUser(member.id).then((isRemove) => {
      if (isRemove) {
        // Refresh team data after successful removal
        getTeam().then((data) => setTeam(data));
      }
    });
  };

  const actionBodyTemplate = (member: TeamResponse) => {
    // Only show remove button if user is not OWNER and current user has permissions
    if (
      member.role === 'OWNER' ||
      !tokenData ||
      tokenData.user.id === member.id ||
      !accountResponse ||
      !isAdminOrOwner(accountResponse.role)
    ) {
      return null;
    }

    return (
      <Button className="action-btn" onClick={() => confirmRemove(member)}>
        <span className="ico--trash ico--xs" />
      </Button>
    );
  };

  if (!team || !accountResponse) {
    return (
      <div className="team-page">
        <Loader />
      </div>
    );
  }

  return (
    <div className="team-page">
      <ConfirmDialog className="dialog-box" />

      <div className="team-page__header">
        <div className="team-page__invite">
          <Button
            label={t('inviteUser')}
            className="btn btn--sm"
            onClick={() => navigate('/settings/team/add')}
            disabled={accountResponse.usersOnAccount >= accountResponse.maxUsers}
          />
        </div>
      </div>

      <div className="team-page__table">
        <DataTable
          stripedRows
          paginator
          paginatorClassName="pagination"
          removableSort
          sortField="firstName"
          sortOrder={1}
          rows={50}
          rowsPerPageOptions={[10, 25, 50]}
          value={team}
          tableStyle={{ minWidth: '50rem' }}
          tableClassName="table"
          className="table-wrapper"
          currentPageReportTemplate={t('rowsNumber') + ':'}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        >
          <Column
            field="firstName"
            header={t('firstName')}
            sortable
            headerClassName="table__header"
            style={{ minWidth: '120px' }}
          />
          <Column
            field="lastName"
            header={t('lastName')}
            sortable
            headerClassName="table__header"
            style={{ minWidth: '120px' }}
          />
          <Column
            field="created"
            header={t('invitedAt')}
            sortable
            body={(member) => format(new Date(Date.now()), 'MMM d, yyyy')}
            headerClassName="table__header"
            style={{ minWidth: '120px' }}
          />
          <Column
            field="email"
            header={t('emailAddress')}
            headerClassName="table__header"
            style={{ minWidth: '200px' }}
          />
          <Column
            field="role"
            header={t('role')}
            sortable
            body={(member) => permissionsMap.get(member.role)}
            headerClassName="table__header"
            style={{ minWidth: '100px' }}
          />
          <Column
            field="isActive"
            header={t('status')}
            sortable
            body={(member) => (
              <span className={member.isActive ? 'status-active' : 'status-inactive'}>
                {member.isActive ? t('active') : t('pending')}
              </span>
            )}
            headerClassName="table__header"
            style={{ minWidth: '100px' }}
          />
          <Column
            header={t('action')}
            body={actionBodyTemplate}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '80px' }}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default TeamPage;
